/**
 * Billing page with usage and plan management.
 */

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useUsage } from '@/hooks/useAnalytics';
import { useMe } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/ui/loading';
import { billingApi } from '@/api/client';
import { PlanCard, type PlanFeature } from '@/components/billing/plan-card';
import { useCheckout } from '@/hooks/useCheckout';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './index.scss';

// Plan features mapping
const PLAN_FEATURES: Record<string, PlanFeature[]> = {
  free: [
    { text: '1 domain', included: true },
    { text: '100 links', included: true },
    { text: '1,000 clicks/month', included: true },
    { text: '1 team member', included: true },
    { text: 'API Access', included: false },
    { text: 'Cascade Graph', included: false },
    { text: 'Real-Time Spark Alerts', included: false },
    { text: 'Enhanced Bot Detection', included: false },
    { text: 'Community Support', included: true },
  ],
  pro: [
    { text: '3 domains', included: true },
    { text: '2,000 links', included: true },
    { text: '50,000 clicks/month', included: true },
    { text: '3 team members', included: true },
    { text: 'API Access', included: true },
    { text: 'Cascade Graph', included: true },
    { text: 'Real-Time Spark Alerts', included: false },
    { text: 'Enhanced Bot Detection', included: false },
    { text: 'Priority Email Support', included: true },
  ],
  agency: [
    { text: '10 domains', included: true },
    { text: '10,000 links', included: true },
    { text: '500,000 clicks/month', included: true },
    { text: '10 team members', included: true },
    { text: 'API Access', included: true },
    { text: 'Cascade Graph', included: true },
    { text: 'Real-Time Spark Alerts', included: true },
    { text: 'Enhanced Bot Detection', included: true },
    { text: 'SLA 24h Support', included: true },
  ],
  scale: [
    { text: 'Unlimited domains', included: true },
    { text: 'Unlimited links', included: true },
    { text: '5,000,000 clicks/month', included: true },
    { text: 'Unlimited team members', included: true },
    { text: 'API Access', included: true },
    { text: 'Cascade Graph', included: true },
    { text: 'Real-Time Spark Alerts', included: true },
    { text: 'Enhanced Bot Detection', included: true },
    { text: 'Dedicated Support Rep', included: true },
  ],
};

const PLAN_NAMES: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  agency: 'Agency',
  scale: 'Scale',
};

export function Billing() {
  const { data: usage, isLoading: usageLoading } = useUsage();
  const { data: me, isLoading: meLoading } = useMe();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const checkout = useCheckout();

  // Fetch plans
  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => billingApi.getPlans(),
  });

  // Handle success/cancel redirects
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      // Show success message (could use a toast library)
      alert('Subscription activated! Your plan has been upgraded.');
      // Remove query params
      setSearchParams({}, { replace: true });
      // Refresh usage data
      window.location.reload();
    } else if (canceled === 'true') {
      // Show cancel message
      alert('Checkout canceled. No changes were made.');
      // Remove query params
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleOpenPortal = async () => {
    setIsLoadingPortal(true);
    try {
      const { url } = await billingApi.getPortalUrl();
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleUpgrade = async (planId: string) => {
    if (planId === 'scale') {
      // Scale plan requires custom pricing
      window.location.href = 'mailto:sales@origyns.io?subject=Scale Plan Inquiry';
      return;
    }

    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/billing?success=true`;
    const cancelUrl = `${baseUrl}/billing?canceled=true`;

    try {
      const checkoutUrl = await checkout.mutateAsync({
        plan: planId,
        successUrl,
        cancelUrl,
      });
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      console.error('Error details:', {
        error,
        planId,
        successUrl,
        cancelUrl,
      });
      const errorMessage = error instanceof Error ? error.message : 'Failed to start checkout. Please try again.';
      alert(errorMessage);
    }
  };

  // Calculate predictive analytics
  const predictions = useMemo(() => {
    if (!usage) return null;

    // Calculate usage percentages
    const usagePercent = {
      domains: (usage.usage.domains / usage.limits.domains) * 100,
      links: (usage.usage.links / usage.limits.links) * 100,
      clicks: (usage.usage.clicks / usage.limits.clicks) * 100,
    };

    // Estimate days until limit (assuming current velocity)
    // Note: This is a simplified calculation. Real implementation would use historical data
    const estimatedDaysUntilLimit = {
      domains: usagePercent.domains > 0 && usagePercent.domains < 100
        ? Math.floor((100 - usagePercent.domains) / (usagePercent.domains / 30))
        : Infinity,
      links: usagePercent.links > 0 && usagePercent.links < 100
        ? Math.floor((100 - usagePercent.links) / (usagePercent.links / 30))
        : Infinity,
      clicks: usagePercent.clicks > 0 && usagePercent.clicks < 100
        ? Math.floor((100 - usagePercent.clicks) / (usagePercent.clicks / 30))
        : Infinity,
    };

    return { usagePercent, estimatedDaysUntilLimit };
  }, [usage]);

  if (usageLoading || meLoading || plansLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const currentPlan = usage?.plan || 'free';

  return (
    <div className="billing">
      <div className="billing__header">
        <h1 className="h1">Billing</h1>
        <p className="subtitle">Manage your subscription and usage</p>
      </div>

      {usage && (
        <>
          <Card>
            <h2 className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>Current Plan</h2>
            <div className="billing__plan">
              <p>{PLAN_NAMES[currentPlan] || 'Free'}</p>
            </div>
            <Button onClick={handleOpenPortal} disabled={isLoadingPortal}>
              {isLoadingPortal ? 'Opening...' : 'Manage Billing'}
            </Button>
          </Card>

          {plans && (
            <div className="billing__plans">
              <h2 className="h3" style={{ marginBottom: 'var(--of-space-6)' }}>Available Plans</h2>
              <div className="billing__plans-grid">
                {plans.map((plan) => {
                  const isCurrent = plan.id === currentPlan;
                  const isPopular = plan.id === 'pro';
                  const hasTrial = plan.id === 'pro';
                  
                  return (
                    <PlanCard
                      key={plan.id}
                      id={plan.id}
                      name={PLAN_NAMES[plan.id] || plan.id}
                      price={plan.price}
                      features={PLAN_FEATURES[plan.id] || []}
                      isCurrentPlan={isCurrent}
                      isPopular={isPopular}
                      hasTrial={hasTrial}
                      trialDays={14}
                      onUpgrade={() => handleUpgrade(plan.id)}
                      isLoading={checkout.isPending}
                      isContactSales={plan.id === 'scale'}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <Card>
            <h2 className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>Usage</h2>
            <div className="billing__usage">
              <div className="billing__usage-bar">
                <div className="billing__usage-bar-label">
                  <span className="billing__usage-bar-label-text">Domains</span>
                  <span className="billing__usage-bar-label-value">
                    {usage.usage.domains} / {usage.limits.domains}
                  </span>
                </div>
                <div className="billing__usage-bar-track">
                  <div
                    className={`billing__usage-bar-fill ${
                      predictions && predictions.usagePercent.domains > 95
                        ? 'billing__usage-bar-fill--critical'
                        : predictions && predictions.usagePercent.domains > 80
                        ? 'billing__usage-bar-fill--warning'
                        : ''
                    }`}
                    style={{
                      width: `${Math.min((usage.usage.domains / usage.limits.domains) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
              <div className="billing__usage-bar">
                <div className="billing__usage-bar-label">
                  <span className="billing__usage-bar-label-text">Links</span>
                  <span className="billing__usage-bar-label-value">
                    {usage.usage.links} / {usage.limits.links}
                  </span>
                </div>
                <div className="billing__usage-bar-track">
                  <div
                    className={`billing__usage-bar-fill ${
                      predictions && predictions.usagePercent.links > 95
                        ? 'billing__usage-bar-fill--critical'
                        : predictions && predictions.usagePercent.links > 80
                        ? 'billing__usage-bar-fill--warning'
                        : ''
                    }`}
                    style={{
                      width: `${Math.min((usage.usage.links / usage.limits.links) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
              <div className="billing__usage-bar">
                <div className="billing__usage-bar-label">
                  <span className="billing__usage-bar-label-text">Clicks</span>
                  <span className="billing__usage-bar-label-value">
                    {usage.usage.clicks.toLocaleString()} / {usage.limits.clicks.toLocaleString()}
                  </span>
                </div>
                <div className="billing__usage-bar-track">
                  <div
                    className={`billing__usage-bar-fill ${
                      predictions && predictions.usagePercent.clicks > 95
                        ? 'billing__usage-bar-fill--critical'
                        : predictions && predictions.usagePercent.clicks > 80
                        ? 'billing__usage-bar-fill--warning'
                        : ''
                    }`}
                    style={{
                      width: `${Math.min((usage.usage.clicks / usage.limits.clicks) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </Card>

          {predictions && (
            <Card>
              <h2 className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>
                Predictive Analytics
              </h2>

              {/* Days until limit warnings */}
              {predictions.estimatedDaysUntilLimit.clicks < 30 &&
                predictions.estimatedDaysUntilLimit.clicks > 0 && (
                  <div
                    className={`billing__warning ${
                      predictions.usagePercent.clicks > 95
                        ? 'billing__warning--critical'
                        : 'billing__warning--warning'
                    }`}
                  >
                    <p>
                      {predictions.usagePercent.clicks > 95
                        ? `⚠️ You'll hit your click limit in approximately ${predictions.estimatedDaysUntilLimit.clicks} days`
                        : `You're projected to hit your click limit in approximately ${predictions.estimatedDaysUntilLimit.clicks} days`}
                    </p>
                    <Button variant="primary">Upgrade Plan</Button>
                  </div>
                )}

              {/* ROI messaging */}
              {predictions.usagePercent.clicks > 80 && (
                <div className="billing__roi">
                  <h4>Upgrade to unlock more features</h4>
                  <p>
                    Upgrade to Agency to unlock Real-Time Spark Alerts - catch viral moments
                    instantly
                  </p>
                  <Button variant="primary">View Plans</Button>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

